// MatterGen conditioning per built-in checkpoint (editable-plan Part 1a, open items 2 and 3).
// Upstream condition fields per built-in checkpoint (mattergen 1.0.3 HF configs,
// research-mattergen-props.md). Mirrored in models/mattergen-service/main.py
// BUILTIN_CONDITIONING; pinned by test_conditioning_allowlist.py, which reads the
// MATTERGEN_CHECKPOINT_CONDITIONING entries below by regex: keep one
// `<checkpoint>: Object.freeze([...])` line per checkpoint.
// Upstream asserts that every key sent is one the checkpoint was trained on; a key it
// knows but the request leaves out falls back to the unconditional embedding.
// This module imports only src/config/elements.js, never the router.
import { ELEMENT_SYMBOLS } from './elements';

export const MATTERGEN_CHECKPOINT_CONDITIONING = Object.freeze({
  mattergen_base: Object.freeze([]),
  mp_20_base: Object.freeze([]),
  dft_band_gap: Object.freeze(['dft_band_gap']),
  ml_bulk_modulus: Object.freeze(['ml_bulk_modulus']),
  chemical_system: Object.freeze(['chemical_system']),
  chemical_system_energy_above_hull: Object.freeze(['chemical_system', 'energy_above_hull']),
  dft_mag_density: Object.freeze(['dft_mag_density']),
  dft_mag_density_hhi_score: Object.freeze(['dft_mag_density', 'hhi_score']),
  space_group: Object.freeze(['space_group']),
});

// Checkpoints whose every listed key is required in Spectra (HHI without hhi_score is a
// duplicate dft_mag_density run).
export const MATTERGEN_REQUIRED_CONDITIONING = Object.freeze({
  dft_mag_density_hhi_score: Object.freeze(['dft_mag_density', 'hhi_score']),
});

// The checkpoint a required-pair checkpoint falls back to when its pair is incomplete (buildRow).
export const MATTERGEN_REQUIRED_CONDITIONING_FALLBACK = Object.freeze({
  dft_mag_density_hhi_score: 'dft_mag_density',
});

// Spec target name (lowercase, exact) -> upstream property key.
export const MATTERGEN_TARGET_PROPERTY_KEYS = Object.freeze({
  band_gap: 'dft_band_gap', bulk_modulus: 'ml_bulk_modulus', energy_above_hull: 'energy_above_hull',
  magnetic_density: 'dft_mag_density', hhi_score: 'hhi_score', space_group: 'space_group',
});

// A custom model's declared `conditioning` (CustomModelsPanel.jsx) -> the built-in
// checkpoint whose key set the row uses.
export const MATTERGEN_CUSTOM_CONDITIONING_TO_CHECKPOINT = Object.freeze({
  unconditional: 'mattergen_base', band_gap: 'dft_band_gap', bulk_modulus: 'ml_bulk_modulus',
  energy_above_hull: 'chemical_system_energy_above_hull', chemical_system: 'chemical_system',
  magnetic_density: 'dft_mag_density', space_group: 'space_group',
});

// Today's manual validation bound on the number of elements.
export const MATTERGEN_MAX_ELEMENTS = 30;

// Upstream mattergen 1.0.3 builds the chemical_system multi-hot over atomic numbers up to
// MAX_ATOMIC_NUM = 100 (mattergen/common/utils/globals.py), so Md (101) and beyond crash the job.
export const MATTERGEN_MAX_ATOMIC_NUMBER = 100;

const ELEMENT_SET = new Set(ELEMENT_SYMBOLS);
const MATTERGEN_ELEMENT_SET = new Set(ELEMENT_SYMBOLS.slice(0, MATTERGEN_MAX_ATOMIC_NUMBER));
const CHEMICAL_SYSTEM_RE = /^[A-Z][a-z]?(-[A-Z][a-z]?)*$/;
const NON_NEGATIVE_KEYS = new Set(['dft_band_gap', 'ml_bulk_modulus', 'energy_above_hull', 'dft_mag_density']);
const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
const isFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v);

/** Why `value` cannot be sent as MatterGen property `key`, or null when it can. The
 * messages complete the validateRowParams path, for example
 * "params.properties.dft_band_gap: must be a finite number >= 0". */
export function mattergenPropertyError(key, value) {
  if (key === 'chemical_system') {
    if (typeof value !== 'string' || !CHEMICAL_SYSTEM_RE.test(value)) {
      return 'must be element symbols joined with "-", for example "Li-O"';
    }
    const symbols = value.split('-');
    if (symbols.length > MATTERGEN_MAX_ELEMENTS) return `must have at most ${MATTERGEN_MAX_ELEMENTS} elements`;
    if (new Set(symbols).size !== symbols.length) return 'must not repeat an element';
    const unknown = symbols.find((s) => !ELEMENT_SET.has(s));
    if (unknown) return `has an unknown element symbol "${unknown}"`;
    const beyond = symbols.find((s) => !MATTERGEN_ELEMENT_SET.has(s));
    if (beyond) return `has element "${beyond}" beyond Fm (atomic number ${MATTERGEN_MAX_ATOMIC_NUMBER}), which MatterGen cannot condition on`;
    return null;
  }
  if (key === 'space_group') {
    return Number.isInteger(value) && value >= 1 && value <= 230 ? null : 'must be an integer from 1 to 230';
  }
  if (key === 'hhi_score') {
    return isFiniteNumber(value) && value >= 0 && value <= 10000 ? null : 'must be a finite number from 0 to 10000';
  }
  if (NON_NEGATIVE_KEYS.has(key)) {
    return isFiniteNumber(value) && value >= 0 ? null : 'must be a finite number >= 0';
  }
  return 'is not a MatterGen conditioning key';
}

/** elements_in filtered to the ELEMENT_SYMBOLS MatterGen can condition on (atomic number
 * <= MATTERGEN_MAX_ATOMIC_NUMBER), de-duplicated in spec order, joined with '-';
 * undefined when none remain or when the result fails mattergenPropertyError (more than 30 symbols). */
export function mattergenChemicalSystem(elementsIn) {
  if (!Array.isArray(elementsIn)) return undefined;
  const symbols = [];
  for (const s of elementsIn) {
    if (MATTERGEN_ELEMENT_SET.has(s) && !symbols.includes(s)) symbols.push(s);
  }
  if (symbols.length === 0) return undefined;
  const value = symbols.join('-');
  return mattergenPropertyError('chemical_system', value) === null ? value : undefined;
}

/** The built-in checkpoint a verified custom MatterGen model's rows use:
 * MATTERGEN_CUSTOM_CONDITIONING_TO_CHECKPOINT[model?.conditioning || 'unconditional'], else 'mattergen_base'.
 * A null or undefined model (a stale manual pick) returns 'mattergen_base'; it never throws. */
export function customMattergenCheckpoint(model) {
  const conditioning = (model && model.conditioning) || 'unconditional';
  return typeof conditioning === 'string' && has(MATTERGEN_CUSTOM_CONDITIONING_TO_CHECKPOINT, conditioning)
    ? MATTERGEN_CUSTOM_CONDITIONING_TO_CHECKPOINT[conditioning]
    : 'mattergen_base';
}
